import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';
import BodyMap from '../components/BodyMap';
import { Card, Button, Input, Textarea, Select, Badge, Modal } from '../components/ui';
import { 
  Plus, 
  Loader2,
  MapPin,
  Palette,
  Calendar,
  Star,
  Trash2,
  Edit
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BodyMapPage = () => {
  const { t } = useTranslation();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedPart, setSelectedPart] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);

  const [formData, setFormData] = useState({
    body_part: '',
    idea: '',
    style: '',
    priority: 1,
    estimated_date: '',
  });

  const bodyPartLabels = {
    head: 'Kafa',
    neck: 'Boyun',
    leftShoulder: 'Sol Omuz',
    rightShoulder: 'Sağ Omuz',
    chest: 'Göğüs',
    leftArm: 'Sol Kol',
    rightArm: 'Sağ Kol',
    leftForearm: 'Sol Ön Kol',
    rightForearm: 'Sağ Ön Kol',
    leftHand: 'Sol El',
    rightHand: 'Sağ El',
    stomach: 'Karın',
    leftThigh: 'Sol Uyluk',
    rightThigh: 'Sağ Uyluk',
    leftCalf: 'Sol Baldır',
    rightCalf: 'Sağ Baldır',
    leftFoot: 'Sol Ayak',
    rightFoot: 'Sağ Ayak',
  };

  const styles = [
    'Blackwork', 'Geometric', 'Traditional', 'Neo Traditional', 
    'Realistic', 'Watercolor', 'Minimalist', 'Japanese', 'Tribal',
    'Fine Line', 'Dotwork', 'Script', 'Abstract'
  ];

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await fetch(`${API_URL}/api/body-map`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setPlans(data);
      }
    } catch (error) {
      console.error('Failed to fetch plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePartClick = (partKey, partLabel) => {
    setSelectedPart(partKey);
    setFormData({
      ...formData,
      body_part: partKey,
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const method = selectedPlan ? 'PUT' : 'POST';
      const url = selectedPlan 
        ? `${API_URL}/api/body-map/${selectedPlan.plan_id}`
        : `${API_URL}/api/body-map`;

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        fetchPlans();
        setShowModal(false);
        resetForm();
      }
    } catch (error) {
      console.error('Failed to save plan:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (planId) => {
    if (!window.confirm('Bu planı silmek istediğinizden emin misiniz?')) return;

    try {
      const response = await fetch(`${API_URL}/api/body-map/${planId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (response.ok) {
        fetchPlans();
      }
    } catch (error) {
      console.error('Failed to delete plan:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      body_part: '',
      idea: '',
      style: '',
      priority: 1,
      estimated_date: '',
    });
    setSelectedPlan(null);
    setSelectedPart(null);
  };

  const openEditModal = (plan) => {
    setSelectedPlan(plan);
    setFormData({
      body_part: plan.body_part || '',
      idea: plan.idea || '',
      style: plan.style || '',
      priority: plan.priority || 1,
      estimated_date: plan.estimated_date ? plan.estimated_date.split('T')[0] : '',
    });
    setShowModal(true);
  };

  const getPriorityColor = (priority) => {
    switch(priority) {
      case 1: return 'destructive';
      case 2: return 'warning';
      case 3: return 'success';
      default: return 'default';
    }
  };

  const getPriorityLabel = (priority) => {
    switch(priority) {
      case 1: return 'Yüksek';
      case 2: return 'Orta';
      case 3: return 'Düşük';
      default: return 'Normal';
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-3xl mb-1" data-testid="bodymap-title">
              Vücut <span className="text-primary">Haritası</span>
            </h1>
            <p className="text-muted-foreground">
              Gelecek dövmelerinizi planlayın
            </p>
          </div>
          <Button onClick={() => { resetForm(); setShowModal(true); }} data-testid="add-plan-btn">
            <Plus className="w-4 h-4 mr-2" />
            Yeni Plan Ekle
          </Button>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Body Map SVG */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <Card className="p-8">
              <h2 className="font-heading text-xl mb-6 text-center">
                İnteraktif Vücut Haritası
              </h2>
              <p className="text-sm text-muted-foreground text-center mb-6">
                Plan eklemek için bir bölgeye tıklayın
              </p>
              <BodyMap 
                selectedParts={plans.map(p => p.body_part)}
                onPartClick={handlePartClick}
                plans={plans}
              />
            </Card>
          </motion.div>

          {/* Plans List */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <h2 className="font-heading text-xl mb-6">Planlarınız ({plans.length})</h2>
            
            {loading ? (
              <div className="flex justify-center py-10">
                <Loader2 className="w-8 h-8 text-primary animate-spin" />
              </div>
            ) : plans.length > 0 ? (
              <div className="space-y-4">
                {plans.map((plan, index) => (
                  <motion.div
                    key={plan.plan_id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Card className="p-4 hover:border-primary/50" data-testid={`plan-card-${index}`}>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <Badge variant={getPriorityColor(plan.priority)}>
                              {getPriorityLabel(plan.priority)}
                            </Badge>
                            <span className="text-sm text-muted-foreground flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {bodyPartLabels[plan.body_part] || plan.body_part}
                            </span>
                          </div>
                          <h3 className="font-medium mb-2">{plan.idea}</h3>
                          <div className="flex items-center gap-4 text-sm text-muted-foreground">
                            {plan.style && (
                              <span className="flex items-center gap-1">
                                <Palette className="w-3 h-3" />
                                {plan.style}
                              </span>
                            )}
                            {plan.estimated_date && (
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {new Date(plan.estimated_date).toLocaleDateString('tr-TR')}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button 
                            size="icon" 
                            variant="ghost"
                            onClick={() => openEditModal(plan)}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button 
                            size="icon" 
                            variant="ghost"
                            className="text-destructive hover:text-destructive"
                            onClick={() => handleDelete(plan.plan_id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </Card>
                  </motion.div>
                ))}
              </div>
            ) : (
              <Card className="p-8 text-center">
                <MapPin className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-heading text-lg mb-2">Henüz Plan Yok</h3>
                <p className="text-muted-foreground mb-4">
                  Vücut haritasından bir bölge seçerek plan ekleyin.
                </p>
              </Card>
            )}
          </motion.div>
        </div>

        {/* Add/Edit Modal */}
        <AnimatePresence>
          {showModal && (
            <Modal
              isOpen={showModal}
              onClose={() => { setShowModal(false); resetForm(); }}
              title={selectedPlan ? 'Planı Düzenle' : 'Yeni Plan Ekle'}
            >
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Vücut Bölgesi *</label>
                  <Select
                    value={formData.body_part}
                    onChange={(e) => setFormData({...formData, body_part: e.target.value})}
                    required
                    data-testid="plan-bodypart-select"
                  >
                    <option value="">Seçin</option>
                    {Object.entries(bodyPartLabels).map(([key, label]) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Dövme Fikri *</label>
                  <Textarea
                    value={formData.idea}
                    onChange={(e) => setFormData({...formData, idea: e.target.value})}
                    placeholder="Dövme fikrinizi açıklayın..."
                    rows={3}
                    required
                    data-testid="plan-idea-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Stil</label>
                    <Select
                      value={formData.style}
                      onChange={(e) => setFormData({...formData, style: e.target.value})}
                      data-testid="plan-style-select"
                    >
                      <option value="">Seçin</option>
                      {styles.map(style => (
                        <option key={style} value={style}>{style}</option>
                      ))}
                    </Select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Öncelik</label>
                    <Select
                      value={formData.priority}
                      onChange={(e) => setFormData({...formData, priority: parseInt(e.target.value)})}
                      data-testid="plan-priority-select"
                    >
                      <option value={1}>Yüksek</option>
                      <option value={2}>Orta</option>
                      <option value={3}>Düşük</option>
                    </Select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Tahmini Tarih</label>
                  <Input
                    type="date"
                    value={formData.estimated_date}
                    onChange={(e) => setFormData({...formData, estimated_date: e.target.value})}
                    data-testid="plan-date-input"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <Button 
                    type="button" 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => { setShowModal(false); resetForm(); }}
                  >
                    İptal
                  </Button>
                  <Button type="submit" className="flex-1" disabled={loading} data-testid="save-plan-btn">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Kaydet'}
                  </Button>
                </div>
              </form>
            </Modal>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
};

export default BodyMapPage;
